import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import RailRow from './RailRow';
import type {
  RunInstrument,
  RunInstrumentsRollup
} from '../hooks/useRunInstruments';
import type { CellStatus } from '@features/executionDashboard/hooks/useCellStatuses';

type Props = {
  instruments: RunInstrument[];
  rollup: RunInstrumentsRollup;
  selectedInstrumentId: string | null;
  onSelect: (woDetailGlobalId: string) => void;
  isLoading: boolean;
  liveCells?: CellStatus[];
  activeInstrumentId?: string | null;
  activeProgress?: number | null;
  isRunLive?: boolean;
  showRerun?: boolean;
  isCellAssigned?: (cell: string | null) => boolean;
  onRerunInstrument?: (instrument: RunInstrument) => void;
};

const InstrumentRail: React.FC<Props> = ({
  instruments,
  rollup,
  selectedInstrumentId,
  onSelect,
  isLoading,
  liveCells = [],
  activeInstrumentId = null,
  activeProgress = null,
  isRunLive = false,
  showRerun = false,
  isCellAssigned,
  onRerunInstrument
}) => {
  const { t } = useTranslation('calibration');

  const liveStatusByCell = useMemo(() => {
    const map = new Map<string, CellStatus['status']>();
    for (const cell of liveCells) {
      map.set(cell.cell, cell.status);
    }
    return map;
  }, [liveCells]);

  const liveRollup = useMemo(() => {
    if (!isRunLive) return null;
    let done = 0;
    let active = 0;
    for (const instrument of instruments) {
      const status = liveStatusByCell.get(instrument.cell ?? '');
      if (status === 'completed' || status === 'failed') done += 1;
      else if (status === 'running') active += 1;
    }
    return { done, active, queued: instruments.length - done - active };
  }, [isRunLive, instruments, liveStatusByCell]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={48} />
        ))}
      </Box>
    );
  }

  if (instruments.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('runPage.noInstruments', {
            defaultValue: 'No line items on this work order.'
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {instruments.map(instrument => (
          <RailRow
            key={instrument.woDetailGlobalId}
            instrument={instrument}
            isSelected={instrument.woDetailGlobalId === selectedInstrumentId}
            onSelect={onSelect}
            liveStatus={
              isRunLive
                ? liveStatusByCell.get(instrument.cell ?? '') ?? 'pending'
                : null
            }
            isActive={instrument.woDetailGlobalId === activeInstrumentId}
            activeProgress={
              instrument.woDetailGlobalId === activeInstrumentId
                ? activeProgress
                : null
            }
            showRerun={showRerun}
            rerunDisabled={!isCellAssigned?.(instrument.cell)}
            onRerun={
              onRerunInstrument
                ? () => onRerunInstrument(instrument)
                : undefined
            }
          />
        ))}
      </Box>
      <Divider />
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('runPage.railRollupInstruments', {
            defaultValue: '{{count}} instruments',
            count: rollup.total
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {liveRollup
            ? t('runPage.railRollupLive', {
                defaultValue:
                  '{{done}} done · {{active}} active · {{queued}} queued',
                done: liveRollup.done,
                active: liveRollup.active,
                queued: liveRollup.queued
              })
            : rollup.latestRunStatus
              ? t('runPage.railRollupLatest', {
                  defaultValue:
                    'Latest run: {{status}} · {{completed}} done · {{failed}} failed',
                  status: rollup.latestRunStatus,
                  completed: rollup.completed,
                  failed: rollup.failed
                })
              : t('runPage.railRollupNoRuns', {
                  defaultValue: 'No runs yet'
                })}
        </Typography>
      </Box>
    </Box>
  );
};

export default InstrumentRail;
